/* eslint-disable */
import { generateDistinctColorsFromBase } from './utils'
import store from '@/store';
import { getTranslation } from '../Base/TranslationUtils';
import communicationService from '@/services/PostMessageCommunation/CommunicationService';
import dataKeys from '@/services/PostMessageCommunation/enums/DataKeys';

const PcaService = {
  sourceObject: {
    sourceData: null,
    targetData: null,

    selectedTargetTitle: null,

    pcaData: null,

    pcaColorMode: 'light',
    isEnableConvexHull: false,
    colorList: generateDistinctColorsFromBase(600)
  },

  // Sets source data
  onSetSourceDatas(sourceData, targetData) {
    this.sourceObject.sourceData = sourceData;
    this.sourceObject.targetData = targetData;
  },

  // Parses the text area data into rows and columns
  parseSourceData(sourceData, isTarget) {
    const lines = sourceData.trim().split('\n');
    const dataArray = [];
    const dataNames = [];
  
    for (let line of lines) {
      const parts = line.trim().split(',');
      
      if(isTarget === false)
        dataNames.push(parts[0])
      else 
        dataNames.push(this.sourceObject.selectedTargetTitle);

      parts.shift();
      const numbers = parts.map(Number);
      dataArray.push(numbers);
    }
  
    return { dataArray, dataNames };
  },

  // Runs the PCA process
  runPCA() {
    const srObject  = this.sourceObject;
    const response = { message: '', isSuccess: true };

    const sourceArrayResponse = this.parseSourceData(srObject.sourceData, false);
    const projectedArrayResponse = this.parseSourceData(srObject.targetData, true);
    
    sourceArrayResponse.dataNames.push(projectedArrayResponse.dataNames[0])
    sourceArrayResponse.dataArray.push(projectedArrayResponse.dataArray[0])

    const dataNames = sourceArrayResponse.dataNames;
    const projectedArray = sourceArrayResponse.dataArray;

    // 1. Create PCA Model
    const pca = new ML.PCA(sourceArrayResponse.dataArray);

    // 2. Get variances and decide the number of components to select
    const explainedVariance = pca.getExplainedVariance();
    let totalVariance = 0;
    let compLength = explainedVariance.findIndex((v) => (totalVariance += v) > 0.98) + 1;

    if (compLength < 1) {
      return { isSuccess: false, message: "Not enough variance explained." };
    }

    // 3. Project new data onto the principal components
    const projected = pca.predict(projectedArray);
 
    // 4. Generate output: Combine names and projection results
    const results = projected.data.map((row, i) => {
      const name = dataNames[i];
      const components = row.slice(0, compLength).map((val) => val.toFixed(6));
      return `${name},${components.join(",")}`;
    });

    srObject.pcaData = results;

    return response;
  },

  computeConvexHull(points) {
    if (points.length < 3) return points;
  
    // 1) Sort by X (if X is the same, sort by Y)
    const sorted = points.slice().sort((a, b) => {
      if (a.x === b.x) return a.y - b.y;
      return a.x - b.x;
    });
  
    // "cross" helper function
    function cross(o, a, b) {
      return (a.x - o.x) * (b.y - o.y) - (a.y - o.y) * (b.x - o.x);
    }
  
    const lower = [];
    for (let p of sorted) {
      while (lower.length >= 2 && cross(lower[lower.length - 2], lower[lower.length - 1], p) <= 0) {
        lower.pop();
      }
      lower.push(p);
    }
  
    const upper = [];
    for (let i = sorted.length - 1; i >= 0; i--) {
      const p = sorted[i];
      while (upper.length >= 2 && cross(upper[upper.length - 2], upper[upper.length - 1], p) <= 0) {
        upper.pop();
      }
      upper.push(p);
    }
  
    // Remove duplicate last elements
    lower.pop();
    upper.pop();
  
    // Complete hull
    return lower.concat(upper);
  },

  // Plots the PCA data
  plotPCA() {
    const srObject = this.sourceObject;
  
    // 1) Extract data
    const projectedNames = srObject.pcaData.map(row => row.split(",")[0]);
    const projected = srObject.pcaData.map(row => row.split(",").slice(1).map(Number));
    const pc1 = projected.map(row => row[0]);
    const pc2 = projected.map(row => row[1]);
  
    // 2) Separate groups
    const groups = {};
    projectedNames.forEach((fullName, index) => {
      const [groupName, sampleName] = fullName.split(":");
      if (!groups[groupName]) {
        groups[groupName] = [];
      }
      groups[groupName].push({
        x: pc1[index],
        y: pc2[index],
        name: fullName
      });
    });
  
    const traces = [];
    const groupCenters = [];
  
    // 3) Scatter trace + center calculation for each group
    let colorIndex = 0;
    for (const groupName in groups) {
      const groupData = groups[groupName];
      const groupX = groupData.map(p => p.x);
      const groupY = groupData.map(p => p.y);
  
      // Group center
      const centerX = groupX.reduce((a, b) => a + b, 0) / groupX.length;
      const centerY = groupY.reduce((a, b) => a + b, 0) / groupY.length;
  
      // Trace for group points
      traces.push({
        x: groupX,
        y: groupY,
        mode: "markers",
        marker: {
          size: groupName === srObject.selectedTargetTitle ? 15 : 8,
          color:
            groupName === srObject.selectedTargetTitle
              ? "#2D2D2D"
              : srObject.colorList[colorIndex % srObject.colorList.length],
          
          opacity: groupName === srObject.selectedTargetTitle ? 1 : 0.5,
          bordercolor: srObject.colorList[colorIndex % srObject.colorList.length],
          borderwidth: 1,  
          borderpad: 2  
        },
        text: groupData.map(p => `${p.name ? p.name : ""}`),
        hoverinfo: "text",
        name: getTranslation(store, groupName),
        visible: srObject.isEnableConvexHull ? 'legendonly' : true,
        
      });
  
      // Save center information
      groupCenters.push({
        x: centerX,
        y: centerY,
        groupName,
        color:
          groupName === srObject.selectedTargetTitle
          ? "#2D2D2D"
          : srObject.colorList[colorIndex % srObject.colorList.length]
      });
  
      // If convex hull needs to be drawn, calculate hull points and add as an additional trace
      if (srObject.isEnableConvexHull) {
        const hullPoints = this.computeConvexHull(groupData);
        if (hullPoints.length >= 3) {
          // Close the hull by adding the first point to the end
          const hullX = hullPoints.map(p => p.x);
          const hullY = hullPoints.map(p => p.y);
          hullX.push(hullX[0]);
          hullY.push(hullY[0]);
  
          traces.push({
            x: hullX,
            y: hullY,
            mode: "lines",
            fill: "toself",
            fillcolor: (groupName === srObject.selectedTargetTitle)
              ? "rgba(45, 45, 45, 0.3)" 
              : srObject.colorList[colorIndex % srObject.colorList.length].replace("rgb", "rgba").replace(")", ",0.5)"),
            line: {
              color:
                groupName === srObject.selectedTargetTitle
                  ? "#2D2D2D"
                  : srObject.colorList[colorIndex % srObject.colorList.length],
              width: 1
            },
            hoverinfo: "skip", // Optionally skip hover on hull
            name: groupName + " hull",
            opacity: 0.3,
            showlegend: false  // Optionally hide from legend
          });
        }
      }
  
      colorIndex++;
    }
  
    // 4) Add center points as a single trace
    traces.push({
      x: groupCenters.map(c => c.x),
      y: groupCenters.map(c => c.y),
      mode: "markers",
      marker: {
        size: 10,
        symbol: "circle",
        color: groupCenters.map(c => c.color),
        opacity: 0.5,
      },
      text: groupCenters.map(c => c.groupName),
      hoverinfo: "text",
      name: "Centers",
    });
  
    // 5) Annotations: Draggable labels and background color
    const layout = {
      title: "",
      xaxis: { zeroline: false },
      yaxis: { zeroline: false },
      hovermode: "closest",
      dragmode: "pan",
      margin: { l: 50, r: 50, t: 50, b: 50 },
      annotations: groupCenters.map(center => ({
        x: center.x,
        y: center.y,
        xref: "x",
        yref: "y",
        text: center.groupName,
        showarrow: true,
        arrowhead: 0,
        axref: "pixel",
        ayref: "pixel",
        ax: center.groupName === srObject.selectedTargetTitle ? 0 : 5,
        ay: center.groupName === srObject.selectedTargetTitle ? -35 : -5,
        font: {
          color: center.color,
          size: center.groupName === srObject.selectedTargetTitle ? 25 : 14,
          family: "Arial"
        },
        bgcolor: "rgba(255,255,255,0.7)",
        bordercolor: center.color,               // Black border
        borderwidth: 1,                       // Border thickness
        borderpad: 2  
      })),
      legend: store.state.appConfig.windowWidth < 769 ? {
        x: 0.5,
        y: -0.2,
        xanchor: 'center',
        yanchor: 'top',
        itemsizing: 'constant',
        whitespace: 'normal',
        itemwidth: 100,
      } : { itemsizing: 'constant' },
      margin: store.state.appConfig.windowWidth < 769 ? { l: 50, r: 50, t: 100, b: 100 } : { },
    };
  
    // 6) Plotly drawing
    const config = {
      edits: {
        annotationTail: true
      },
      modeBarButtonsToRemove: store.state.appConfig.is_embedded_in_mh_app ? ['toImage']: [],
      showSendToCloud: false,
      responsive: true,
      scrollZoom: true,
      toImageButtonOptions: {
        format: 'png',
        filename: 'PCA',
        height: 1200,
        width: 1600,
        scale: 1
      },
    };
  
    const graphDiv = document.getElementById("pcaPlotContainer");
    Plotly.newPlot(graphDiv, traces, layout, config).then(() => {
      this.addButtonListener(graphDiv);
      this.addViewChangeListener(graphDiv);
    });
  },

  addButtonListener(graphDiv) {
    const downloadButton = graphDiv.querySelector('.modebar-btn[data-title="Download plot as a png"]');
    if (downloadButton) {
      downloadButton.addEventListener('click', () => {
        communicationService.sendDataToParent({
          key: dataKeys.STATISTICS,
          value: 'Iframe.ancientDnaGeneticDistanceMapsDownloadPlotClick',
        });
      });
    }

    const producedPlotlyButton = graphDiv.querySelector('.modebar-btn[data-title="Produced with Plotly"]');
    if (producedPlotlyButton) {
      producedPlotlyButton.addEventListener('click', () => {
        communicationService.sendDataToParent({
          key: dataKeys.STATISTICS,
          value: 'Iframe.ancientDnaGeneticDistanceMapsProducedWithPlotlyClick',
        });
      });
    }

    graphDiv.on('plotly_legendclick', () => {
      communicationService.sendDataToParent({
        key: dataKeys.STATISTICS,
        value: 'Iframe.ancientDnaGeneticDistanceMapsIsolateARegion',
      });
    });
  },

  addViewChangeListener(graphDiv) {
    // Triggered when the plot is zoomed, panned, or relayout happens
    graphDiv.on('plotly_relayout', () => {
      communicationService.sendDataToParent({
        key: dataKeys.STATISTICS,
        value: 'Iframe.ancientDnaGeneticDistanceMapsViewChanged',
      });
    });

    // Triggered after the plot is redrawn
    graphDiv.on('plotly_afterplot', () => {
      communicationService.sendDataToParent({
        key: dataKeys.STATISTICS,
        value: 'Iframe.ancientDnaGeneticDistanceMapsViewChanged',
      });
    });

    // Triggered when the plot is resized
    graphDiv.on('plotly_resized', () => {
      communicationService.sendDataToParent({
        key: dataKeys.STATISTICS,
        value: 'Iframe.ancientDnaGeneticDistanceMapsViewChanged',
      });
    });
  }
};

export default PcaService;
